<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useEventListener } from '@vueuse/core';

import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import { AppImg, AppLinkButton, AppMarkdownWrapper } from '@libs/ui';
import type { DestinationInformation } from '@libs/trip-planner-interfaces';

import { useHotelDetailsModalsStore } from '../stores';

import DestinationInformationSectionMarkdownStyles from './DestinationInformationSectionMarkdownStyles.vue';

interface DestinationInformationSectionProps {
  destinationInformation: DestinationInformation;
}
const props = defineProps<DestinationInformationSectionProps>();

const hotelDetailsModalsStore = useHotelDetailsModalsStore();
const browserStore = useBrowserStore();

const destinationInformationDescriptionRef = ref<HTMLDivElement | null>(null);
const destinationInfromationImageRef = ref<HTMLDivElement | null>(null);

const visibleDescriptionLinesAmount = ref<number | undefined>(undefined);
const shouldShowMoreInfoButton = ref<boolean>(false);

function calculateVisibleDescriptionLines(): void {
  if (
    !destinationInfromationImageRef.value ||
    !destinationInformationDescriptionRef.value
  ) {
    return;
  }

  if (!browserStore.isLargerThan899px) {
    visibleDescriptionLinesAmount.value = 10;
    shouldShowMoreInfoButton.value =
      destinationInformationDescriptionRef.value.scrollHeight >=
      destinationInformationDescriptionRef.value.clientHeight;
    return;
  }
  const headerHeight = 40;
  const lineHeight = 24;
  const moreInfoButtonHeight = 36;

  const availableDescriptionHeight =
    destinationInfromationImageRef.value.clientHeight -
    headerHeight -
    moreInfoButtonHeight;
  const calculatedLines = Math.floor(availableDescriptionHeight / lineHeight);
  const isOverflowing =
    destinationInformationDescriptionRef.value.scrollHeight >
    availableDescriptionHeight;

  if (isOverflowing) {
    visibleDescriptionLinesAmount.value = calculatedLines;
    shouldShowMoreInfoButton.value = true;
  } else {
    visibleDescriptionLinesAmount.value = undefined;
    shouldShowMoreInfoButton.value = false;
  }
}

useEventListener(window, 'resize', calculateVisibleDescriptionLines);

onMounted(() => {
  calculateVisibleDescriptionLines();
});
</script>

<template>
  <div class="destination-information">
    <div class="content-wrapper">
      <span class="title">
        {{
          $t('planner.about_destination', [
            props.destinationInformation.destination,
          ])
        }}
      </span>

      <div ref="destinationInformationDescriptionRef" class="description">
        <DestinationInformationSectionMarkdownStyles>
          <AppMarkdownWrapper
            :source="props.destinationInformation.description"
          />
        </DestinationInformationSectionMarkdownStyles>
      </div>
      <AppLinkButton
        v-if="shouldShowMoreInfoButton"
        :text="$t('planner.show_more')"
        class="show-more-button"
        size="large"
        @click="
          hotelDetailsModalsStore.openFullDestinationDescriptionModal(
            props.destinationInformation
          )
        "
      />
    </div>
    <div
      v-if="props.destinationInformation.image"
      ref="destinationInfromationImageRef"
      class="image-wrapper"
    >
      <AppImg
        :src="props.destinationInformation.image"
        alt="destination image"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.destination-information {
  display: grid;
  grid-template-columns: 1fr 40%;

  gap: var(--spacing-48);
  padding: var(--spacing-32) 0;
  color: var(--color-neutral-70);

  border-top: 1px solid var(--color-scrollbar);

  overflow: hidden;

  @include for-less-or-equal-than-1200-px {
    margin: 0 var(--spacing-16);
  }

  @include for-less-or-equal-than-899-px {
    display: flex;
    flex-direction: column-reverse;
    gap: var(--spacing-16);
  }
  .content-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-12);
    .title {
      @include font-label-xxl-bold;
      @include for-less-or-equal-than-899-px {
        @include font-body-l-bold;
      }
    }

    .show-more-button {
      width: fit-content;
      margin-top: var(--spacing-4);
    }
  }
  .description {
    display: -webkit-box;
    line-clamp: v-bind(visibleDescriptionLinesAmount);
    -webkit-line-clamp: v-bind(visibleDescriptionLinesAmount);
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;

    color: var(--color-neutral-70);
    @include font-body-l-light;

    @include for-less-or-equal-than-899-px {
      @include font-body-m-light;
    }
  }
  .image-wrapper {
    border-radius: var(--border-radius-12);
    overflow: hidden;
    aspect-ratio: 5/3;
    @include for-less-or-equal-than-899-px {
      aspect-ratio: 7 / 3;
    }
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}
</style>
